import React, { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  X,
  TrendingDown,
  TrendingUp,
  DollarSign,
  AlertTriangle,
  Droplets,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Activity,
  Target,
  Zap,
} from 'lucide-react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { analyticsEngine } from '../utils/analytics/analyticsEngine';

interface AdvancedAnalyticsProps {
  well: any;
  productionData: any[];
  onClose?: () => void;
}

export default function AdvancedAnalytics({
  well,
  productionData,
  onClose,
}: AdvancedAnalyticsProps) {
  const [expandedSection, setExpandedSection] = useState<string | null>('decline');
  const [declineModel, setDeclineModel] = useState<'exponential' | 'hyperbolic' | 'harmonic'>('hyperbolic');

  // Calculate analytics
  const analytics = useMemo(() => {
    try {
      // Prepare production data
      const formattedData = productionData.map(entry => ({
        date: new Date(entry.timestamp),
        oil: entry.oilProduction || 0,
        gas: entry.staticPressure * 10 || 0, // Approximate gas
        water: entry.waterProduction || 0,
        days: 1,
      }));

      if (formattedData.length < 3) {
        return null;
      }

      // Decline Curve Analysis
      const declineCurve = analyticsEngine.analyzeDeclineCurve(
        well.id,
        well.name,
        formattedData,
        declineModel,
        24
      );

      // EUR Calculation
      const currentProduction = {
        oil: formattedData.reduce((sum, d) => sum + d.oil, 0),
        gas: formattedData.reduce((sum, d) => sum + d.gas, 0),
      };
      const eur = analyticsEngine.calculateEUR(
        well.id,
        well.name,
        declineCurve,
        currentProduction
      );

      // ROI Dashboard
      const roi = analyticsEngine.calculateROI(
        well.id,
        well.name,
        {
          drilling: well.totalInvestment * 0.6 || 500000,
          completion: well.totalInvestment * 0.3 || 300000,
          facilities: well.totalInvestment * 0.1 || 100000,
          equipment: 50000,
          total: well.totalInvestment || 900000,
        },
        currentProduction.oil * 75, // Historical revenue
        currentProduction.oil * 25, // Historical opex
        declineCurve,
        75, // Oil price
        25, // Opex per barrel
        10 // Discount rate
      );

      // Predictive Maintenance
      const latestEntry = formattedData[formattedData.length - 1];
      const maintenance = analyticsEngine.analyzePredictiveMaintenance(
        well.id,
        well.name,
        {
          pressure: well.staticPressure || 100,
          production: well.oilProduction || 50,
          runtime: 5000,
          temperature: 160,
          vibration: 3.5,
          efficiency: 85,
        },
        []
      );

      // Water Cut Analysis
      const waterCut = analyticsEngine.analyzeWaterCut(
        well.id,
        well.name,
        formattedData
      );

      // Production Forecast
      const forecast = analyticsEngine.forecastProduction(
        well.id,
        well.name,
        declineCurve,
        {
          oilPrice: 75,
          gasPrice: 3.5,
          gasOilRatio: 1000,
          operatingDays: 30,
        }
      );

      return {
        declineCurve,
        eur,
        roi,
        maintenance,
        waterCut,
        forecast,
      };
    } catch (error) {
      console.error('Analytics calculation error:', error);
      return null;
    }
  }, [well, productionData, declineModel]);

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  if (!analytics) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-16 w-16 text-orange-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Insufficient Data
            </h2>
            <p className="text-gray-600 mb-4">
              Need at least 3 production entries to perform analytics
            </p>
            {onClose && (
              <Button onClick={onClose} className="w-full">
                Close
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4 shadow-lg sticky top-0 z-10">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <BarChart3 className="h-6 w-6" />
            <div>
              <h2 className="text-xl font-bold">Advanced Analytics</h2>
              <p className="text-xs text-purple-100">{well.name}</p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/20 active:bg-white/30"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {/* Decline Curve Analysis */}
        <AnalyticsSection
          icon={<TrendingDown className="h-5 w-5 text-blue-600" />}
          title="Decline Curve Analysis"
          expanded={expandedSection === 'decline'}
          onToggle={() => toggleSection('decline')}
          badge={
            <Badge className={`${
              analytics.declineCurve.quality.confidence === 'high'
                ? 'bg-green-100 text-green-800'
                : analytics.declineCurve.quality.confidence === 'medium'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-red-100 text-red-800'
            }`}>
              R² = {analytics.declineCurve.quality.r2.toFixed(3)}
            </Badge>
          }
        >
          <DeclineCurveChart
            data={analytics.declineCurve}
            model={declineModel}
            onModelChange={setDeclineModel}
          />
        </AnalyticsSection>

        {/* EUR Calculation */}
        <AnalyticsSection
          icon={<Target className="h-5 w-5 text-green-600" />}
          title="Estimated Ultimate Recovery (EUR)"
          expanded={expandedSection === 'eur'}
          onToggle={() => toggleSection('eur')}
        >
          <EURVisualization eur={analytics.eur} />
        </AnalyticsSection>

        {/* ROI Dashboard */}
        <AnalyticsSection
          icon={<DollarSign className="h-5 w-5 text-emerald-600" />}
          title="ROI & Economics"
          expanded={expandedSection === 'roi'}
          onToggle={() => toggleSection('roi')}
          badge={
            <Badge className="bg-emerald-100 text-emerald-800">
              ROI: {analytics.roi.metrics.roi.toFixed(1)}%
            </Badge>
          }
        >
          <ROIDashboard roi={analytics.roi} />
        </AnalyticsSection>

        {/* Predictive Maintenance */}
        <AnalyticsSection
          icon={<Zap className="h-5 w-5 text-orange-600" />}
          title="Predictive Maintenance"
          expanded={expandedSection === 'maintenance'}
          onToggle={() => toggleSection('maintenance')}
          badge={
            <Badge className={`${
              analytics.maintenance.riskLevel === 'critical'
                ? 'bg-red-100 text-red-800'
                : analytics.maintenance.riskLevel === 'high'
                ? 'bg-orange-100 text-orange-800'
                : analytics.maintenance.riskLevel === 'medium'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-green-100 text-green-800'
            }`}>
              Risk: {analytics.maintenance.riskScore.toFixed(0)}
            </Badge>
          }
        >
          <PredictiveMaintenanceView maintenance={analytics.maintenance} />
        </AnalyticsSection>

        {/* Water Cut Trends */}
        <AnalyticsSection
          icon={<Droplets className="h-5 w-5 text-cyan-600" />}
          title="Water Cut Analysis"
          expanded={expandedSection === 'watercut'}
          onToggle={() => toggleSection('watercut')}
          badge={
            <Badge className="bg-cyan-100 text-cyan-800">
              {analytics.waterCut.thresholds.current.toFixed(1)}%
            </Badge>
          }
        >
          <WaterCutChart waterCut={analytics.waterCut} />
        </AnalyticsSection>

        {/* Production Forecast */}
        <AnalyticsSection
          icon={<Activity className="h-5 w-5 text-indigo-600" />}
          title="24-Month Production Forecast"
          expanded={expandedSection === 'forecast'}
          onToggle={() => toggleSection('forecast')}
        >
          <ProductionForecastChart forecast={analytics.forecast} />
        </AnalyticsSection>
      </div>
    </div>
  );
}

// Analytics Section Component
function AnalyticsSection({
  icon,
  title,
  badge,
  expanded,
  onToggle,
  children,
}: any) {
  return (
    <Card className={`transition-all ${expanded ? 'ring-2 ring-purple-500' : ''}`}>
      <CardHeader
        className="pb-3 cursor-pointer active:bg-gray-50"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 flex-1">
            {icon}
            <CardTitle className="text-base">{title}</CardTitle>
          </div>
          <div className="flex items-center space-x-2">
            {badge}
            {expanded ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </div>
      </CardHeader>
      {expanded && <CardContent className="pt-0">{children}</CardContent>}
    </Card>
  );
}

// Decline Curve Chart
function DeclineCurveChart({ data, model, onModelChange }: any) {
  const chartData = [
    ...data.historical.map((h: any) => ({
      time: h.time,
      actual: h.rate,
      type: 'Historical',
    })),
    ...data.forecast.map((f: any) => ({
      time: f.time,
      forecast: f.rate,
      type: 'Forecast',
    })),
  ];

  return (
    <div className="space-y-4">
      {/* Model Selector */}
      <div className="flex space-x-2">
        {(['exponential', 'hyperbolic', 'harmonic'] as const).map((m) => (
          <button
            key={m}
            onClick={() => onModelChange(m)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
              model === m
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {m.charAt(0).toUpperCase() + m.slice(1)}
          </button>
        ))}
      </div>

      {/* Chart */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="time" label={{ value: 'Days', position: 'bottom' }} />
            <YAxis label={{ value: 'Rate (BPD)', angle: -90, position: 'left' }} />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="actual"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ r: 3 }}
              name="Historical"
            />
            <Line
              type="monotone"
              dataKey="forecast"
              stroke="#a855f7"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              name="Forecast"
            />
            <ReferenceLine
              x={data.historical[data.historical.length - 1].time}
              stroke="#94a3b8"
              strokeDasharray="3 3"
              label="Today"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-blue-50 rounded-lg p-3">
          <p className="text-xs text-blue-700">Initial Rate</p>
          <p className="text-lg font-bold text-blue-900">
            {data.parameters.qi.toFixed(1)} BPD
          </p>
        </div>
        <div className="bg-purple-50 rounded-lg p-3">
          <p className="text-xs text-purple-700">Decline Rate</p>
          <p className="text-lg font-bold text-purple-900">
            {(data.parameters.di * 100).toFixed(1)}%/yr
          </p>
        </div>
        <div className="bg-green-50 rounded-lg p-3">
          <p className="text-xs text-green-700">Economic Limit</p>
          <p className="text-lg font-bold text-green-900">
            {data.economicLimit.rate.toFixed(1)} BPD
          </p>
        </div>
        <div className="bg-orange-50 rounded-lg p-3">
          <p className="text-xs text-orange-700">EUR</p>
          <p className="text-lg font-bold text-orange-900">
            {(data.economicLimit.cumulativeProduction / 1000).toFixed(0)}K BBL
          </p>
        </div>
      </div>
    </div>
  );
}

// EUR Visualization
function EURVisualization({ eur }: any) {
  return (
    <div className="space-y-4">
      {/* Oil EUR */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Oil Recovery</span>
          <span className="text-sm font-bold text-green-600">
            {eur.oil.percentRecovered.toFixed(1)}%
          </span>
        </div>
        <div className="relative w-full h-8 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="absolute top-0 left-0 h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full transition-all duration-500"
            style={{ width: `${Math.min(100, eur.oil.percentRecovered)}%` }}
          />
          <div className="absolute inset-0 flex items-center justify-center text-xs font-medium text-gray-700">
            {eur.oil.produced.toLocaleString()} / {eur.oil.eur.toLocaleString()} BBL
          </div>
        </div>
      </div>

      {/* Gas EUR */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Gas Recovery</span>
          <span className="text-sm font-bold text-blue-600">
            {eur.gas.percentRecovered.toFixed(1)}%
          </span>
        </div>
        <div className="relative w-full h-8 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500"
            style={{ width: `${Math.min(100, eur.gas.percentRecovered)}%` }}
          />
          <div className="absolute inset-0 flex items-center justify-center text-xs font-medium text-gray-700">
            {(eur.gas.produced / 1000).toFixed(0)}K / {(eur.gas.eur / 1000).toFixed(0)}K MCF
          </div>
        </div>
      </div>

      {/* Remaining Reserves */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-green-50 rounded-lg p-3">
          <p className="text-xs text-green-700">Remaining Oil</p>
          <p className="text-lg font-bold text-green-900">
            {eur.oil.remaining.toLocaleString()} BBL
          </p>
        </div>
        <div className="bg-blue-50 rounded-lg p-3">
          <p className="text-xs text-blue-700">Remaining Gas</p>
          <p className="text-lg font-bold text-blue-900">
            {(eur.gas.remaining / 1000).toFixed(0)}K MCF
          </p>
        </div>
      </div>
    </div>
  );
}

// ROI Dashboard
function ROIDashboard({ roi }: any) {
  return (
    <div className="space-y-4">
      {/* Key Metrics */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-emerald-50 rounded-lg p-3">
          <p className="text-xs text-emerald-700">NPV @ {roi.discountRate}%</p>
          <p className="text-lg font-bold text-emerald-900">
            ${(roi.metrics.npv / 1000).toFixed(0)}K
          </p>
        </div>
        <div className="bg-blue-50 rounded-lg p-3">
          <p className="text-xs text-blue-700">IRR</p>
          <p className="text-lg font-bold text-blue-900">
            {roi.metrics.irr.toFixed(1)}%
          </p>
        </div>
        <div className="bg-purple-50 rounded-lg p-3">
          <p className="text-xs text-purple-700">ROI</p>
          <p className="text-lg font-bold text-purple-900">
            {roi.metrics.roi.toFixed(1)}%
          </p>
        </div>
        <div className="bg-orange-50 rounded-lg p-3">
          <p className="text-xs text-orange-700">Payback</p>
          <p className="text-lg font-bold text-orange-900">
            {roi.metrics.paybackMonths === Infinity
              ? 'N/A'
              : `${roi.metrics.paybackMonths} mo`}
          </p>
        </div>
      </div>

      {/* Revenue vs Costs */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Total Revenue</span>
          <span className="font-bold text-green-600">
            ${(roi.revenue.total / 1000).toFixed(0)}K
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Operating Costs</span>
          <span className="font-bold text-red-600">
            ${(roi.opex.total / 1000).toFixed(0)}K
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Investment</span>
          <span className="font-bold text-gray-900">
            ${(roi.investment.total / 1000).toFixed(0)}K
          </span>
        </div>
        <div className="border-t pt-2 flex items-center justify-between">
          <span className="font-medium text-gray-900">Net Income</span>
          <span className="font-bold text-emerald-600 text-lg">
            ${((roi.revenue.total - roi.opex.total - roi.investment.total) / 1000).toFixed(0)}K
          </span>
        </div>
      </div>
    </div>
  );
}

// Predictive Maintenance View
function PredictiveMaintenanceView({ maintenance }: any) {
  return (
    <div className="space-y-4">
      {/* Risk Score */}
      <div className="text-center">
        <div
          className={`inline-flex items-center justify-center w-32 h-32 rounded-full ${
            maintenance.riskLevel === 'critical'
              ? 'bg-red-100'
              : maintenance.riskLevel === 'high'
              ? 'bg-orange-100'
              : maintenance.riskLevel === 'medium'
              ? 'bg-yellow-100'
              : 'bg-green-100'
          }`}
        >
          <div className="text-center">
            <div
              className={`text-4xl font-bold ${
                maintenance.riskLevel === 'critical'
                  ? 'text-red-600'
                  : maintenance.riskLevel === 'high'
                  ? 'text-orange-600'
                  : maintenance.riskLevel === 'medium'
                  ? 'text-yellow-600'
                  : 'text-green-600'
              }`}
            >
              {maintenance.riskScore.toFixed(0)}
            </div>
            <div className="text-xs text-gray-600 uppercase">Risk Score</div>
          </div>
        </div>
      </div>

      {/* Contributing Factors */}
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-gray-700">Contributing Factors</h4>
        {maintenance.factors.map((factor: any) => (
          <div key={factor.factor} className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-600">{factor.factor}</span>
              <Badge
                className={`${
                  factor.status === 'critical'
                    ? 'bg-red-100 text-red-800'
                    : factor.status === 'warning'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-green-100 text-green-800'
                }`}
              >
                {factor.status}
              </Badge>
            </div>
            <div className="relative w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`absolute top-0 left-0 h-full rounded-full ${
                  factor.status === 'critical'
                    ? 'bg-red-500'
                    : factor.status === 'warning'
                    ? 'bg-yellow-500'
                    : 'bg-green-500'
                }`}
                style={{ width: `${factor.contribution}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Recommendation */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-sm font-semibold text-blue-900 mb-1">Recommendation</p>
        <p className="text-sm text-blue-700">{maintenance.predictions.recommendedAction}</p>
        <p className="text-xs text-blue-600 mt-2">
          Est. Cost: ${maintenance.predictions.estimatedCost.toLocaleString()}
        </p>
      </div>
    </div>
  );
}

// Water Cut Chart
function WaterCutChart({ waterCut }: any) {
  const chartData = [
    ...waterCut.historical.map((h: any) => ({
      date: new Date(h.date).toLocaleDateString('en-US', { month: 'short' }),
      waterCut: h.waterCut,
      type: 'Historical',
    })),
    ...waterCut.forecast.map((f: any) => ({
      date: new Date(f.date).toLocaleDateString('en-US', { month: 'short' }),
      waterCut: f.waterCut,
      type: 'Forecast',
    })),
  ];

  return (
    <div className="space-y-4">
      {/* Trend Badge */}
      <div className="flex items-center justify-between">
        <Badge
          className={`${
            waterCut.trend.direction === 'increasing'
              ? 'bg-red-100 text-red-800'
              : waterCut.trend.direction === 'decreasing'
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-800'
          }`}
        >
          {waterCut.trend.direction === 'increasing' && <TrendingUp className="h-4 w-4 mr-1" />}
          {waterCut.trend.direction === 'decreasing' && <TrendingDown className="h-4 w-4 mr-1" />}
          {waterCut.trend.direction.toUpperCase()} {Math.abs(waterCut.trend.rate).toFixed(1)}%/mo
        </Badge>
        <span className="text-sm text-gray-600">
          12-mo projection: {waterCut.trend.projection12Mo.toFixed(1)}%
        </span>
      </div>

      {/* Chart */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis label={{ value: 'Water Cut (%)', angle: -90 }} domain={[0, 100]} />
            <Tooltip />
            <Legend />
            <ReferenceLine y={waterCut.thresholds.warning} stroke="#f59e0b" strokeDasharray="3 3" label="Warning" />
            <ReferenceLine y={waterCut.thresholds.critical} stroke="#ef4444" strokeDasharray="3 3" label="Critical" />
            <Area
              type="monotone"
              dataKey="waterCut"
              stroke="#06b6d4"
              fill="#06b6d4"
              fillOpacity={0.3}
              name="Water Cut"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Status */}
      <div className="grid grid-cols-3 gap-2">
        <div className="text-center">
          <p className="text-xs text-gray-600">Current</p>
          <p className="text-lg font-bold text-cyan-600">
            {waterCut.thresholds.current.toFixed(1)}%
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-600">Warning</p>
          <p className="text-lg font-bold text-yellow-600">
            {waterCut.thresholds.warning}%
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-600">Critical</p>
          <p className="text-lg font-bold text-red-600">
            {waterCut.thresholds.critical}%
          </p>
        </div>
      </div>
    </div>
  );
}

// Production Forecast Chart
function ProductionForecastChart({ forecast }: any) {
  const chartData = forecast.forecast.map((f: any, index: number) => ({
    month: `M${f.month}`,
    oil: f.oil,
    revenue: f.revenue,
    upper: forecast.confidence.upper[index],
    lower: forecast.confidence.lower[index],
  }));

  return (
    <div className="space-y-4">
      {/* Chart */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Area
              type="monotone"
              dataKey="upper"
              stroke="none"
              fill="#a5b4fc"
              fillOpacity={0.3}
              name="Upper Bound"
            />
            <Area
              type="monotone"
              dataKey="oil"
              stroke="#6366f1"
              fill="#6366f1"
              fillOpacity={0.5}
              name="Forecast"
            />
            <Area
              type="monotone"
              dataKey="lower"
              stroke="none"
              fill="#ffffff"
              fillOpacity={1}
              name="Lower Bound"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-indigo-50 rounded-lg p-3">
          <p className="text-xs text-indigo-700">24-Month Production</p>
          <p className="text-lg font-bold text-indigo-900">
            {(forecast.forecast.reduce((sum: number, f: any) => sum + f.oil, 0) / 1000).toFixed(0)}K BBL
          </p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-3">
          <p className="text-xs text-emerald-700">Total Revenue</p>
          <p className="text-lg font-bold text-emerald-900">
            ${(forecast.forecast.reduce((sum: number, f: any) => sum + f.revenue, 0) / 1000).toFixed(0)}K
          </p>
        </div>
      </div>

      {/* Assumptions */}
      <div className="bg-gray-50 rounded-lg p-3 text-xs space-y-1">
        <p className="font-semibold text-gray-700">Assumptions:</p>
        <p className="text-gray-600">Oil Price: ${forecast.assumptions.oilPrice}/BBL</p>
        <p className="text-gray-600">Gas Price: ${forecast.assumptions.gasPrice}/MCF</p>
        <p className="text-gray-600">Operating Days: {forecast.assumptions.operatingDays}/month</p>
      </div>
    </div>
  );
}
